import { Component, Prop, State, h } from '@stencil/core';

import { Appointment } from '../../../interfaces';
import { trackAppointmentRequestSubmit } from '../../../tracking/tracking.dealer';
import { formatTime } from '../../../utils/utils.time-format';
import { DateEntry } from '../../form-controls/two-weeks-date-select/two-weeks-date-select';
import { currentDatePlusSeven, expirationPlusOne, generateTwoWeekEntries, getCalendarEntry } from '../../form-controls/two-weeks-date-select/utils/calendar';
import { actionItems } from '../../form-controls/types';

@Component({
  tag: 'appointment-request',
  styleUrl: 'appointment-request.css',
})
export class AppointmentRequest {
  @Prop() dealerId: number;
  @Prop() expirationDate: string = currentDatePlusSeven();
  @Prop() schedule: { day: string; startTime: string; endTime: string }[] = [];
  @Prop() whenRequested: (dayOfWeek, timeFrame) => void;

  @State() activeDealerId: number = 0;
  @State() apptClick: boolean = false; // Todo: not sure about this, will revisit
  @State() canSubmit: boolean = false;
  @State() displayTime: boolean = true; // show/hide time frame if there is availability
  @State() isSendingRequestAppointment: boolean = false;
  @State() endTime: number = 18;
  @State() selectedTime: string = '';
  @State() startTime: number = 10;
  @State() selectedDate;
  @State() timeFrame: string = '';

  private dayMonthYear: { day: number | string; month: number | string; year: number | string };
  private entries: DateEntry[] = [];

  twoWeeksDateChanged = ({ day, month, year }) => {
    const date = { day, month, year };
    // date
    this.dayMonthYear = date;
    this.selectedDate = date;

    // time
    this.selectedTime = '';
    this.timeFrame = '';
    this.startTime = this.getTime(date).start;
    this.endTime = this.getTime(date).end;

    this.canSubmit = this.canSchedule();
  };

  timeChanged = (time: string) => {
    this.selectedTime = time;
    this.timeFrame = this.selectedTime;
    this.canSubmit = this.canSchedule();
  };

  actions: actionItems = {
    dateChanged: this.twoWeeksDateChanged,
    timeChanged: this.timeChanged,
  };

  // check if dealer id has changed
  hasDealerChanged() {
    return this.activeDealerId !== this.dealerId;
  }

  componentWillRender() {
    // wait for the dealers schedule
    if (this.schedule.length) {
      const scheduleDays = this.getScheduleDays(this.schedule);
      const entries = generateTwoWeekEntries(getCalendarEntry(new Date()), scheduleDays, expirationPlusOne(new Date(this.expirationDate)));
      const availableDay = entries.find((days) => !days.isDisabled);

      if (this.hasDealerChanged()) {
        if (availableDay) {
          // entries
          this.entries = this.getEntries(entries, availableDay);
          this.dayMonthYear = availableDay;

          // time
          this.displayTime = true;
          this.startTime = this.getTime(availableDay).start;
          this.endTime = this.getTime(availableDay).end;
        } else {
          // when no dates are available
          this.entries = entries;
          this.displayTime = false;
          this.dayMonthYear = undefined;
        }

        // time
        this.selectedTime = '';
        this.timeFrame = '';
        this.canSubmit = this.canSchedule();

        // set dealer id to active if dealerId has changed
        this.activeDealerId = this.dealerId;
      } else {
        // entries
        this.entries = this.getEntries(entries, this.dayMonthYear);

        // set dealer id to active if dealerId has changed
        this.activeDealerId = this.dealerId;
      }
    }
  }

  getEntries(dateEntries, dateObj) {
    return dateEntries.map((date) => {
      return { ...date, select: dateObj.day === date.day };
    });
  }

  getTime(date) {
    const selectedDay = new Date(date.year, date.month - 1, date.day).toLocaleString('en-us', { weekday: 'long' });
    const selectedDateTime = this.schedule.find((day) => day.day === selectedDay);

    return {
      start: formatTime(selectedDateTime.startTime),
      end: formatTime(selectedDateTime.endTime),
    };
  }

  getScheduleDays(schedule) {
    return schedule.map((a) => a.day);
  }

  // submit form
  private onRequestClick = () => {
    this.isSendingRequestAppointment = true;
    trackAppointmentRequestSubmit();
    const { dayMonthYear, timeFrame } = this;

    // Todo, remove enum reliance.
    this.whenRequested(timeFrame as Appointment.AppointmentTimeFrame, dayMonthYear);
  };

  canSchedule() {
    // disable the form submit until ready
    return !!this.timeFrame && !!this.dayMonthYear;
  }

  render() {
    return (
      <div class="flex flex-col items-center mt-4 w-full max-w-md">
        <h3 class="text-center text-xl sm:text-2xl mb-1">Request an Appointment</h3>
        <div class="text-center text-md mb-2 sm:mb-4">Your Offer Is Good For 7 Days</div>
        <two-weeks-date-select actions={this.actions} entries={this.entries} expirationDate={this.expirationDate} />

        {this.displayTime && (
          <div class="border-solid box-border border border-clrs-grey p-2 md:p-4 mt-4 mb-4 w-full max-w-md md:w-96">
            <time-select actions={this.actions} start={this.startTime} end={this.endTime} selectedTime={this.selectedTime} />
          </div>
        )}

        <ico-button
          buttonStyle="secondary"
          buttonSize="large"
          disabled={!this.canSubmit || this.isSendingRequestAppointment}
          whenClicked={this.onRequestClick}
          label={this.isSendingRequestAppointment ? 'Sending' : 'Request Appointment'}
        />
      </div>
    );
  }
}
